"use client";
import { clickEvent } from "@/lib/atInternetTags";
import type { Destinations as DestinationsData } from "@/queries/getDestinations";
import clsx from "clsx";
import Link from "next/link";
import { usePathname } from "next/navigation";
import type { ReactNode } from "react";
import { PrimaryButton } from "ui/components";
import { ArrowLeftIcon, PhoneIcon, UserIcon } from "ui/iconsN";
import { SearchBar } from "../search/searchbar";
import { ContainerRest } from "./ContainerRest";
import { menuStore } from "./MenuInner";
import { MenuLink } from "./MenuLink";
import { SMGLogo } from "./SMGLogo";
import { SMGNavigationMenu } from "./SMGNavigationMenu";
import { SMGMobileMenu } from "./SMGmobileMenu";

type Props = {
  infoHeader: ReactNode;
  basket: ReactNode;
  catalogueImage: ReactNode;
  destinations: DestinationsData;
};
export const SMGHeader = ({ infoHeader, basket, destinations, catalogueImage }: Props) => {
  const pathname = usePathname();
  const isTravelPage = pathname.startsWith("/sur-mesure/voyage/");

  return (
    <ContainerRest infoHeader={infoHeader} search={<SearchBar />}>
      <div className="z-10 bg-inherit transition-colors">
        <div className="flex items-center max-xl:justify-between m-auto h-14 xl:h-20 max-w-(--breakpoint-xxl) px-4 xl:pl-16">
          <div className="flex gap-4 items-center w-24 xl:hidden">
            <Link href="/account/">
              <UserIcon className="stroke-pr-1" />
            </Link>
          </div>
          <Link href={{ pathname: "/", query: {} }} className="flex items-center gap-1 no-underline shrink-0">
            <ArrowLeftIcon className="stroke-pr-1" />
            <span className="text-pr-1 text-xs/[14px] font-semibold whitespace-pre-wrap">
              Revenir sur le
              <br />
              site principal
            </span>
          </Link>
          <div className="bg-tx-2 w-0.5 h-12 mx-4 md:mx-6" />
          <SMGLogo pathname="/sur-mesure/groupe/" />
          <div className="grow xl:hidden" />
          <div className="hidden xl:flex basis-full justify-start gap-4 ml-16">
            <SMGNavigationMenu
              destinations={destinations}
              isHeaderTransparent={false}
              catalogueImage={catalogueImage}
              isHomepage={false}
            />
          </div>
          <div className="hidden xl:flex items-center gap-1.5">
            <MenuLink
              isHeaderTransparent={false}
              href="/account/"
              onClick={() =>
                clickEvent({
                  event: "click.navigation",
                  click: "mon_compte",
                  click_chapter1: "menu",
                })
              }
            >
              <UserIcon />
              Mon compte
            </MenuLink>
            <div className="h-8 w-px bg-bg-2 z-10" />
            <div className="relative group ml-1.5">
              <a
                href="tel:+33145619090"
                className={clsx(
                  "h-12 rounded-lg px-4 no-underline whitespace-nowrap flex items-center gap-3 border transition-colors",
                  "text-pr-1 stroke-pr-1 border-pr-1 bg-white",
                  "hover:text-white hover:stroke-white",
                  "[clip-path:inset(0px_round_8px)]",
                )}
                onClick={() => menuStore.set(null)}
              >
                <div className="absolute -bottom-1 left-1/2 w-[200%] aspect-square btn-phone-nav rounded-full -translate-x-1/2 translate-y-1/2 transition-transform scale-0 group-hover:scale-100" />
                <div
                  className={clsx(
                    "absolute -inset-px border rounded-lg transition-colors group-hover:shadow-[0_-1.5px_0_0_rgba(120,40,3,0.24)_inset,0_1.5px_0_0_rgba(242,144,108,0.32)_inset]",
                    "border-pr-1",
                    "group-hover:border-pr-2",
                  )}
                />
                <PhoneIcon className="z-10 group-hover:drop-shadow-sm" />
                <span className="text-sm font-semibold z-10 group-hover:drop-shadow-sm">01 45 61 90 90</span>
              </a>
              <div
                className={clsx(
                  "bg-primary rounded-lg text-white hidden group-hover:block text-sm font-medium left-1/2 p-2 absolute text-center top-[calc(100%+12px)] -translate-x-1/2 whitespace-nowrap z-50",
                  "after:absolute after:left-1/2 after:-translate-x-1/2 after:bottom-full after:border-[5px] after:border-transparent after:border-b-primary",
                )}
              >
                <p>Nous restons à votre écoute</p>
                <p>du lundi au jeudi entre 9h30-18h00</p>
                <p>et le vendredi entre 9h30-17h00</p>
              </div>
            </div>
            <Link href={isTravelPage ? "#form" : "/sur-mesure/groupe/#form"}>
              <PrimaryButton className="whitespace-nowrap" direction="up">
                Demandez un devis
              </PrimaryButton>
            </Link>
          </div>
          <div className="flex items-center justify-end xl:hidden w-24">
            <SMGMobileMenu destinations={destinations} />
          </div>
        </div>
      </div>
    </ContainerRest>
  );
};
